import { useCallback, useRef, useState } from 'react';
import { api, WS_URL, type AnalysisResponse } from './api';

export interface ProgressStep { step: string; message: string; at: number }

/**
 * Run an analysis over the WebSocket so agent progress streams in; falls back
 * to the POST endpoint if the socket cannot connect within a few seconds.
 */
export function useStreamingAnalysis() {
  const [result, setResult] = useState<AnalysisResponse | null>(null);
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  const ask = useCallback(async (query: string, extra: Record<string, unknown> = {}) => {
    setLoading(true); setError(null); setResult(null); setSteps([]);
    socketRef.current?.close();

    const viaPost = async () => {
      try { setResult(await api.analyze(query, extra)); } catch (e) { setError((e as Error).message); } finally { setLoading(false); }
    };

    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };
      let ws: WebSocket;
      try { ws = new WebSocket(`${WS_URL}/ws/analysis`); } catch { void viaPost().then(done); return; }
      socketRef.current = ws;
      const fallback = window.setTimeout(() => { if (ws.readyState !== WebSocket.OPEN) { ws.close(); void viaPost().then(done); } }, 3000);
      ws.onopen = () => { window.clearTimeout(fallback); ws.send(JSON.stringify({ query, ...extra })); };
      ws.onmessage = (ev: MessageEvent<string>) => {
        // A frame that will not parse must not throw out of the handler: that
        // leaves the socket open, `settled` false and the page spinning forever.
        let frame: { type: string; step?: string; message?: string; data?: AnalysisResponse; status?: number };
        try {
          frame = JSON.parse(ev.data);
        } catch {
          ws.close();
          void viaPost().then(done);
          return;
        }
        if (frame.type === 'progress') setSteps((s) => [...s, { step: frame.step ?? '', message: frame.message ?? '', at: Date.now() }]);
        else if (frame.type === 'result') { setResult(frame.data ?? null); setLoading(false); ws.close(); done(); }
        else if (frame.type === 'error') { setError(frame.message ?? 'Something went wrong.'); setLoading(false); ws.close(); done(); }
      };
      ws.onerror = () => { window.clearTimeout(fallback); if (!settled) { ws.close(); void viaPost().then(done); } };
      ws.onclose = () => { if (!settled) { void viaPost().then(done); } };
    });
  }, []);

  return { ask, result, steps, loading, error };
}
